import { createApp, type App as VueApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import PrimeVue from 'primevue/config'
import Aura from '@primevue/themes/aura'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'

import App from './App.vue'
import i18n from './i18n'
import { initKeycloak, login } from './lib/keycloak'

import 'primeicons/primeicons.css'
import './styles/main.css'

// Prevent multiple bootstrap executions
declare global {
  interface Window {
    __bootstrapRunning__?: boolean
    __bootstrapComplete__?: boolean
    __redirectingToLogin__?: boolean
  }
}

// Show loading indicator
function showLoading(message = 'Chargement...') {
  const loadingEl = document.getElementById('app')
  if (loadingEl) {
    loadingEl.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;background:#f9fafb;">
        <div style="text-align:center;">
          <div style="width:48px;height:48px;border:3px solid #e5e7eb;border-top-color:#3b82f6;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto;"></div>
          <p style="margin-top:16px;color:#6b7280;">${message}</p>
        </div>
      </div>
      <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
    `
  }
}

// Show error screen
function showError(title: string, message: string) {
  const errorEl = document.getElementById('app')
  if (errorEl) {
    errorEl.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;background:#fef2f2;">
        <div style="text-align:center;max-width:400px;padding:20px;">
          <div style="width:48px;height:48px;background:#fee2e2;border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;">
            <svg style="width:24px;height:24px;color:#dc2626;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <h2 style="font-size:18px;font-weight:600;color:#991b1b;margin-bottom:8px;">${title}</h2>
          <p style="color:#b91c1c;margin-bottom:16px;">${message}</p>
          <button onclick="window.location.reload()" style="background:#dc2626;color:white;padding:8px 16px;border:none;border-radius:6px;cursor:pointer;">
            Réessayer
          </button>
        </div>
      </div>
    `
  }
}

// Configure Vue app plugins (excluding router)
function configureApp(app: VueApp) {
  const pinia = createPinia()
  app.use(pinia)

  app.use(VueQueryPlugin, {
    queryClientConfig: {
      defaultOptions: {
        queries: {
          staleTime: 1000 * 60 * 5,
          retry: 2,
        },
      },
    },
  })

  app.use(PrimeVue, {
    theme: {
      preset: Aura,
      options: {
        prefix: 'p',
        darkModeSelector: '.dark',
        cssLayer: false,
      }
    }
  })
  app.use(ToastService)
  app.use(ConfirmationService)
  app.use(i18n)
}

async function bootstrap() {
  // Prevent multiple executions
  if (window.__bootstrapRunning__) {
    console.log('[DiabAssist] Bootstrap already running, skipping')
    return
  }
  if (window.__bootstrapComplete__) {
    console.log('[DiabAssist] Bootstrap already complete, skipping')
    return
  }
  if (window.__redirectingToLogin__) {
    console.log('[DiabAssist] Already redirecting to login, skipping')
    showLoading('Redirection vers la connexion...')
    return
  }

  window.__bootstrapRunning__ = true
  showLoading('Authentification...')

  try {
    // =================================================================
    // STEP 1: Initialize Keycloak FIRST (before any Vue Router import)
    // Official docs: "Make sure the adapter is always initialized
    // before initializing a router of your client-side framework"
    // =================================================================
    console.log('[DiabAssist] Step 1: Initializing Keycloak...')
    const authenticated = await initKeycloak()
    console.log('[DiabAssist] Keycloak initialized, authenticated:', authenticated)

    // =================================================================
    // STEP 2: Handle authentication state
    // =================================================================
    if (!authenticated) {
      // Check if we're returning from an auth error
      const urlParams = new URLSearchParams(window.location.search)
      const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'))

      const hasError = urlParams.has('error') || hashParams.has('error')

      if (hasError) {
        const errorDesc = urlParams.get('error_description') || hashParams.get('error_description') || 'Erreur inconnue'
        console.error('[DiabAssist] Auth error:', errorDesc)
        showError('Erreur d\'authentification', errorDesc)
        window.__bootstrapRunning__ = false
        return
      }

      // Not authenticated and no error - redirect to login
      console.log('[DiabAssist] Not authenticated, redirecting to Keycloak login...')
      window.__redirectingToLogin__ = true
      showLoading('Redirection vers la connexion...')
      await login()
      // Don't reset flags - page will redirect
      return
    }

    // =================================================================
    // STEP 3: User is authenticated - NOW import and setup Vue Router
    // Dynamic import ensures router is created AFTER Keycloak is ready
    // =================================================================
    console.log('[DiabAssist] Step 3: Loading application...')
    showLoading('Chargement de l\'application...')

    // Dynamic import of router - this is the key fix!
    const { default: router } = await import('./router')
    const { useAuthStore } = await import('./stores/auth')

    // Create Vue app
    const app = createApp(App)

    // Configure plugins (pinia, vue-query, primevue, i18n)
    configureApp(app)

    // Add router AFTER Keycloak is fully initialized
    app.use(router)

    // Sync auth store with Keycloak state
    const authStore = useAuthStore()
    await authStore.initialize()
    console.log('[DiabAssist] Auth store synced, user:', authStore.userName)

    // Mount app
    app.mount('#app')
    console.log('[DiabAssist] App mounted successfully')

    window.__bootstrapComplete__ = true
    window.__bootstrapRunning__ = false

  } catch (error) {
    console.error('[DiabAssist] Bootstrap failed:', error)
    window.__bootstrapRunning__ = false
    showError(
      'Erreur de démarrage',
      error instanceof Error ? error.message : 'Une erreur inattendue s\'est produite'
    )
  }
}

bootstrap()
